<template>
  <div class="itv-root resizable-columns">
    <!-- Above the table, like Generate Invoices' selection action bar. -->
    <InvoiceActionsToolbar
      :count="selection.selectedCount.value"
      :all-selected="selection.allSelected.value"
      :selecting-all="selection.selectingAll.value"
      :actions="toolbarActions"
      :busy-action="busyAction"
      noun="keys"
      @action="runAction"
      @clear="selection.clear"
    />

    <BaseDataTable
      ref="dataTableRef"
      :columns="ItemizedViewColumns"
      :items="itemsProvider"
      :total="total"
      :initial-page-size="100"
      :page-size-options="[100, 200, 500, 1000]"
    >
      <template v-slot:head(select)>
        <InvoicingSelectCheckbox
          :checked="selection.headerState.value === 'all'"
          :indeterminate="selection.headerState.value === 'some'"
          :disabled="!total || selection.selectingAll.value"
          aria-label="Select all keys"
          @toggle="selection.toggleHeader"
        />
      </template>
      <template v-slot:cell(select)="{ item }">
        <InvoicingSelectCheckbox
          :checked="selection.isSelected(item)"
          :aria-label="`Select ${item.bdx_key || 'key'}`"
          @toggle="selection.toggleRow(item)"
        />
      </template>

      <template v-slot:cell(bdx_key)="{ item }">
        <span class="itv-mono">{{ item.bdx_key || '—' }}</span>
      </template>

      <template v-slot:cell(invoice_reference)="{ item }">
        <span class="itv-mono">{{ item.invoice_reference || '—' }}</span>
      </template>

      <template v-slot:cell(reporting_period)="{ item }">{{ item.reporting_period || '—' }}</template>
      <template v-slot:cell(managing_agent)="{ item }">{{ item.managing_agent || '—' }}</template>
      <template v-slot:cell(tpa_name)="{ item }">{{ item.tpa_name || '—' }}</template>
      <template v-slot:cell(broker)="{ item }">{{ item.broker || '—' }}</template>
      <template v-slot:cell(billing_type)="{ item }">{{ item.billing_type || '—' }}</template>
      <template v-slot:cell(syndicate_number)="{ item }">{{ item.syndicate_number || '—' }}</template>
      <template v-slot:cell(charge_description)="{ item }">{{ item.charge_description || '—' }}</template>
      <template v-slot:cell(currency_type)="{ item }">{{ item.currency_type || '—' }}</template>

      <template v-slot:cell(amount)="{ item }">
        <span class="itv-amount">{{ formatCurrency(item.amount, item.currency_type) }}</span>
      </template>

      <!-- LIQ-2465: same per-key flag as the Invoice view accordion. -->
      <template v-slot:cell(received)="{ item }">
        <InvoicingSelectCheckbox
          v-if="isTpa"
          :checked="!!receivedFor(item)"
          :disabled="!!savingReceived[item.charge_id]"
          :aria-label="`Mark ${item.bdx_key || 'key'} as received`"
          @toggle="toggleReceived(item)"
        />
        <span v-else :class="receivedFor(item) ? 'itv-yes' : 'itv-no'">{{ receivedFor(item) ? 'Yes' : 'No' }}</span>
      </template>
    </BaseDataTable>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, getCurrentInstance } from '@vue/composition-api'

import { BaseDataTable } from '@components/base'
import { enableColumnResize } from '@components/base/base-data-table/useResizableColumns'

import InvoicingService from '@services/InvoicingService'
import InvoiceActionsToolbar from './InvoiceActionsToolbar.vue'
import InvoicingSelectCheckbox from './InvoicingSelectCheckbox.vue'
import { formatCurrency, saveBlobResponse } from './useInvoiceExport'
import { getItemizedViewColumns, SELECT_COLUMN } from './InvoicingTab.constants'
import {
  useInvoicingRole,
  useInvoicingSelection,
  criteriaSignature,
  requestErrorMessage,
  responseBody,
} from './useInvoicingSelection'

export default defineComponent({
  name: 'ItemizedInvoicesTable',

  components: {
    BaseDataTable,
    InvoiceActionsToolbar,
    InvoicingSelectCheckbox,
  },

  setup() {
    const instance = getCurrentInstance()
    const proxy = instance.proxy
    const { isTpa } = useInvoicingRole()

    const ItemizedViewColumns = computed(() => [SELECT_COLUMN, ...getItemizedViewColumns()])

    const total = ref(0)
    const dataTableRef = ref(null)
    const exporting = ref(false)

    // Every column key here already matches the backend's query param
    // name 1:1 (see InvoiceChargeFlatListView) — including broker, which
    // Lincoln resolves and filters/sorts server-side on its own (unlike
    // tpa_name, which Lincoln has no company-name field for, so
    // InvoiceChargesFlatListProxyView translates a tpa_name filter to
    // tpa_company_id_in itself — same resolved-at-the-proxy-layer
    // treatment as InvoiceViewTable.vue's tpa_name column).
    // reporting_period is a range filter — BaseDataTableTable.vue (the
    // shared component this table goes through) already splits its
    // [from, to] value into reporting_period_gt/reporting_period_lt
    // before this itemsProvider ever sees it, so those two keys just
    // need to be in the whitelist below, same as any other column.
    const FILTERABLE_KEYS = [
      'bdx_key', 'invoice_reference', 'managing_agent', 'tpa_name', 'broker', 'charge_description', 'currency_type', 'amount',
      'reporting_period_gt', 'reporting_period_lt', 'billing_type', 'syndicate_number', 'received',
    ]

    // Kept in sync on every itemsProvider call so exportXlsx can re-issue
    // the exact same query, minus pagination, as an Excel download — see
    // InvoicingTab.vue's Export button. Also what "select all" and the
    // selection's Download XLS re-issue.
    const lastQueryParams = ref({})

    // Received changes made here since the page loaded, by charge_id — so
    // a tick shows immediately without refetching the page.
    const receivedOverrides = ref({})
    const receivedFor = (item) =>
      Object.prototype.hasOwnProperty.call(receivedOverrides.value, item.charge_id)
        ? receivedOverrides.value[item.charge_id]
        : !!item.received

    const selection = useInvoicingSelection({
      idOf: (row) => String(row.charge_id),
      metaOf: (row) => ({ charge_id: row.charge_id, received: receivedFor(row) }),
      fetchAllMatching: async () => {
        const { sort_by, sort_desc, ...criteria } = lastQueryParams.value
        const resp = await InvoicingService.getChargeSelection(criteria)
        return responseBody(resp).data || []
      },
    })

    let lastCriteria = null

    // Server-side filter/sort/pagination — b-table's own #table-busy slot
    // (wired up in BaseDataTableTable.vue) shows a loading state while
    // this promise is pending, so no separate loading state needed here.
    const itemsProvider = async (context) => {
      const { filter, filters, currentPage, perPage, sortBy, sortDesc } = context

      // A selection survives paging and sorting, but not a change to what
      // the table is showing (LIQ-2071 AC16/17).
      const criteria = criteriaSignature(filter, filters)
      if (lastCriteria !== null && criteria !== lastCriteria) selection.clear()
      lastCriteria = criteria

      const filterParams = {}
      if (filters && filters.length) {
        filters.forEach((f) => {
          if (FILTERABLE_KEYS.includes(f.key)) filterParams[f.key] = f.value
        })
      }

      const queryParams = {
        sort_by: sortBy,
        sort_desc: !!sortDesc,
        search: filter || undefined,
        ...filterParams,
      }
      lastQueryParams.value = queryParams

      try {
        const resp = await InvoicingService.getInvoiceCharges({
          ...queryParams,
          page: currentPage,
          page_size: perPage,
        })
        const inner = resp.data && resp.data.data
        const rows = inner && inner.data
        total.value = (inner && inner.count) || 0
        selection.total.value = total.value
        receivedOverrides.value = {}
        return Array.isArray(rows) ? rows : []
      } catch (e) {
        console.error('Failed to load invoice charges:', e)
        total.value = 0
        selection.total.value = 0
        return []
      }
    }

    // ── Actions ──────────────────────────────────────────────────────────

    const busyAction = ref(null)

    const toast = (message, variant = 'success', title = 'Invoice charges') => {
      proxy.$bvToast && proxy.$bvToast.toast(message, { title, variant, autoHideDelay: 5000, solid: true })
    }

    const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

    // Download XLS for everyone; marking Received is a TPA action
    // (LIQ-2465). Each Received action is offered while it would change
    // at least one selected key — marking an already-received key as
    // received is harmless, so a mixed "select all" isn't blocked.
    const toolbarActions = computed(() => {
      const list = [{ key: 'xls', label: '↓ XLS' }]
      if (!isTpa.value) return list
      const rows = selection.selectedRows.value
      list.push({
        key: 'received', label: 'Mark Received',
        disabled: rows.every((r) => r.received), disabledReason: 'Every selected key is already received.',
      })
      list.push({
        key: 'not_received', label: 'Mark Not Received',
        disabled: rows.every((r) => !r.received), disabledReason: 'No selected key is marked received.',
      })
      return list
    })

    const refreshTable = () => {
      if (dataTableRef.value) dataTableRef.value.handleRefresh()
    }

    const downloadSelected = async () => {
      // Everything matching is selected — the plain filtered export is the
      // same file without sending every id.
      const resp = selection.allSelected.value
        ? await InvoicingService.exportInvoiceChargesXlsx(lastQueryParams.value)
        : await InvoicingService.exportSelectedChargesXlsx(lastQueryParams.value, selection.selectedIds.value)
      saveBlobResponse(resp, 'invoice-charges.xlsx')
    }

    const markSelected = async (received) => {
      const resp = await InvoicingService.markChargesReceived(selection.selectedIds.value, received)
      const result = responseBody(resp).data || {}
      toast(`${plural(result.updated || 0, 'key')} marked as ${received ? 'received' : 'not received'}.`, 'success', 'Received')
      refreshTable()
    }

    const runAction = async (key) => {
      if (busyAction.value || !selection.selectedCount.value) return
      busyAction.value = key
      try {
        if (key === 'xls') await downloadSelected()
        else if (key === 'received') await markSelected(true)
        else if (key === 'not_received') await markSelected(false)
        // LIQ-2071 AC20 — the selection is done with once its action is.
        selection.clear()
      } catch (e) {
        console.error(`Invoice charge action ${key} failed:`, e)
        toast(await requestErrorMessage(e, 'Something went wrong. Please try again.'), 'danger', 'Error')
      } finally {
        busyAction.value = null
      }
    }

    const savingReceived = ref({})

    const toggleReceived = async (item) => {
      const chargeId = item.charge_id
      const received = !receivedFor(item)
      savingReceived.value = { ...savingReceived.value, [chargeId]: true }
      try {
        await InvoicingService.markChargesReceived([chargeId], received)
        receivedOverrides.value = { ...receivedOverrides.value, [chargeId]: received }
        selection.refreshMeta([item])
      } catch (e) {
        toast(await requestErrorMessage(e, 'Could not update Received. Please try again.'), 'danger', 'Received')
      } finally {
        savingReceived.value = { ...savingReceived.value, [chargeId]: false }
      }
    }

    // Re-issues the current filter/search/sort as a full (unpaginated)
    // Excel download — called from InvoicingTab.vue's shared Export
    // button via this component's template ref.
    const exportXlsx = async () => {
      exporting.value = true
      try {
        const resp = await InvoicingService.exportInvoiceChargesXlsx(lastQueryParams.value)
        saveBlobResponse(resp, 'invoice-charges.xlsx')
      } catch (e) {
        console.error('Failed to export invoice charges:', e)
        throw e
      } finally {
        exporting.value = false
      }
    }

    // BaseDataTableTable.vue renders <thead> synchronously from columns
    // (only <tbody> waits on itemsProvider), so the header cells this
    // attaches resize handles to already exist by the time this runs.
    onMounted(() => {
      enableColumnResize(instance.proxy.$el)
    })

    return {
      ItemizedViewColumns,
      total,
      dataTableRef,
      itemsProvider,

      selection,
      toolbarActions,
      busyAction,
      runAction,

      isTpa,
      receivedFor,
      savingReceived,
      toggleReceived,

      exporting,
      exportXlsx,
      formatCurrency,
    }
  },
})
</script>

<style scoped>
/* Matches Generate Invoices' (PendingInvoicesTable/ApprovedDispatchedInvoicesTable)
   .gi-root table styling exactly, including its narrow first-column
   checkbox rule. */
.itv-root >>> .table {
  font-size: 11px;
  table-layout: fixed;
}
.itv-root >>> .table td > div {
  padding: 8px 10px !important;
}
.itv-root >>> .table th:first-child,
.itv-root >>> .table td:first-child {
  width: 28px !important;
  min-width: 28px !important;
  max-width: 28px !important;
  padding-left: 4px !important;
  padding-right: 4px !important;
}
.itv-root >>> .table td:first-child > div {
  padding: 0 !important;
}
.itv-root >>> .table th {
  font-size: 11px !important;
  white-space: normal;
  word-break: break-word;
  vertical-align: middle;
}
/* A value wider than the column's current width never wraps onto a
   second line (which would grow the row) — it's clipped at the column's
   current width instead. Widening the column (see
   useResizableColumns.js) is the only way to reveal the rest of it;
   there's no hover reveal. */
.itv-root >>> .table td {
  font-size: 11px !important;
  white-space: nowrap;
  overflow: hidden;
  vertical-align: middle;
}
.itv-root >>> .table thead th {
  font-size: 10px !important;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 8px 6px;
}
.itv-root >>> .card .card-body {
  padding: 0 1.5rem !important;
}
.itv-root >>> .container {
  padding: 0 !important;
  max-width: initial !important;
}

.itv-mono { color: #1c4a50; font-size: 10px; font-family: monospace; }
.itv-amount { font-weight: 600; color: #1c4a50; font-size: 11px; }
.itv-yes { color: #2dce89; font-weight: 600; }
.itv-no { color: #8898aa; }
</style>
